import {constructUsing, createMap, forMember, mapFrom, type Mapper} from '@automapper/core';
import type {IMapping} from '@igp/shared/auto-mapper';
import type {WeeklySummaryExport} from '@igp/shared/contract/pam';
import {formatTimestamp} from '@igp/shared/utils';

import type {
    AgentNegativeCarryoverReportExtended,
    NegativeCarryoverReportDownloadFields,
    NegativeCarryoverReportDownloadModel,
    NegativeCarryoverReportQueryFields,
    NegativeCarryoverReportSortKeys,
    NegativeCarryoverReportViewModel,
    NegativeCarryoverReportViewModelKeys,
    NegativeCarryoverWeeklySummaryDownloadFields,
    NegativeCarryoverWeeklySummaryDownloadModel,
    NegativeCarryoverWeeklySummaryQueryFields,
    NegativeCarryoverWeeklySummaryViewModelKeys,
} from './types';

export const negativeCarryoverReportMappingKeys = {
    viewModel: 'NegativeCarryoverReportViewModel',
    serverModel: 'AgentNegativeCarryoverReportExtended',
    downloadModel: 'NegativeCarryoverReportDownloadModel',
    downloadFields: 'NegativeCarryoverReportDownloadFields',
    viewModelKeys: 'NegativeCarryoverReportViewModelKeys',
    queryKeys: 'NegativeCarryoverReportQueryFields',
    sortKeys: 'NegativeCarryoverReportSortKeys',
    weeklySummaryServerModel: 'WeeklySummaryExport',
    weeklySummaryDownloadModel: 'NegativeCarryoverWeeklySummaryDownloadModel',
    weeklySummaryViewModelKeys: 'NegativeCarryoverWeeklySummaryViewModelKeys',
    weeklySummaryQueryKeys: 'NegativeCarryoverWeeklySummaryQueryFields',
    weeklySummaryDownloadFields: 'NegativeCarryoverWeeklySummaryDownloadFields',
} as const;

export class NegativeCarryoverReportMapping implements IMapping {
    createMapping(mapper: Mapper) {
        createMap<AgentNegativeCarryoverReportExtended, NegativeCarryoverReportViewModel>(
            mapper,
            negativeCarryoverReportMappingKeys.serverModel,
            negativeCarryoverReportMappingKeys.viewModel,
            forMember(
                vm => vm.id,
                mapFrom(m => m.id)
            ),
            forMember(
                vm => vm.agentUid,
                mapFrom(m => m.agent_uid)
            ),
            forMember(
                vm => vm.debtAfterPayment,
                mapFrom(m => m.debt_after_payment)
            ),
            forMember(
                vm => vm.debtBeforePayment,
                mapFrom(m => m.debt_before_payment)
            ),
            forMember(
                vm => vm.debtTransaction,
                mapFrom(m => m.debt_transaction)
            ),
            forMember(
                vm => vm.isGeneral,
                mapFrom(m => m.is_general)
            ),
            forMember(
                vm => vm.lastUpdated,
                mapFrom(m => m.last_updated)
            ),
            forMember(
                vm => vm.totalNetShare,
                mapFrom(m => m.total_net_share)
            ),
            forMember(
                vm => vm.weekStartDateTs,
                mapFrom(m => m.week_start_date_ts)
            ),
            forMember(
                vm => vm.weeklyActuallyPaid,
                mapFrom(m => m.weekly_actually_paid)
            )
        );
        createMap<AgentNegativeCarryoverReportExtended, NegativeCarryoverReportDownloadModel>(
            mapper,
            negativeCarryoverReportMappingKeys.serverModel,
            negativeCarryoverReportMappingKeys.downloadModel,
            forMember(
                vm => vm.id,
                mapFrom(m => m.id)
            ),
            forMember(
                vm => vm.agentUid,
                mapFrom(m => m.agent_uid)
            ),
            forMember(
                vm => vm.debtAfterPayment,
                mapFrom(m => m.debt_after_payment?.toString())
            ),
            forMember(
                vm => vm.debtBeforePayment,
                mapFrom(m => m.debt_before_payment?.toString())
            ),
            forMember(
                vm => vm.debtTransaction,
                mapFrom(m => m.debt_transaction?.toString())
            ),
            forMember(
                vm => vm.totalNetShare,
                mapFrom(m => m.total_net_share?.toString())
            ),
            forMember(
                vm => vm.weekStartDateTs,
                mapFrom(m => formatTimestamp(m.week_start_date_ts, 'date'))
            ),
            forMember(
                vm => vm.weeklyActuallyPaid,
                mapFrom(m => m.weekly_actually_paid?.toString())
            )
        );

        createMap<NegativeCarryoverReportViewModelKeys, NegativeCarryoverReportQueryFields>(
            mapper,
            negativeCarryoverReportMappingKeys.viewModelKeys,
            negativeCarryoverReportMappingKeys.queryKeys,
            constructUsing(m => {
                const mapper: Record<NegativeCarryoverReportViewModelKeys, NegativeCarryoverReportQueryFields> = {
                    id: 'items.id',
                    agentUid: 'items.agent_uid',
                    debtAfterPayment: 'items.debt_after_payment',
                    debtBeforePayment: 'items.debt_before_payment',
                    debtTransaction: 'items.debt_transaction',
                    totalNetShare: 'items.total_net_share',
                    weekStartDateTs: 'items.week_start_date_ts.seconds',
                    weeklyActuallyPaid: 'items.weekly_actually_paid',
                };
                return mapper[m];
            })
        );
        createMap<NegativeCarryoverReportViewModelKeys, NegativeCarryoverReportDownloadFields>(
            mapper,
            negativeCarryoverReportMappingKeys.viewModelKeys,
            negativeCarryoverReportMappingKeys.downloadFields,
            constructUsing(m => {
                const mapper: Record<NegativeCarryoverReportViewModelKeys, NegativeCarryoverReportDownloadFields> = {
                    id: 'id',
                    agentUid: 'agent_uid',
                    debtAfterPayment: 'debt_after_payment',
                    debtBeforePayment: 'debt_before_payment',
                    debtTransaction: 'debt_transaction',
                    totalNetShare: 'total_net_share',
                    weekStartDateTs: 'week_start_date_ts.seconds',
                    weeklyActuallyPaid: 'weekly_actually_paid',
                };
                return mapper[m];
            })
        );

        createMap<NegativeCarryoverReportViewModelKeys, NegativeCarryoverReportSortKeys>(
            mapper,
            negativeCarryoverReportMappingKeys.viewModelKeys,
            negativeCarryoverReportMappingKeys.sortKeys,
            constructUsing(m => {
                const mapper: Record<NegativeCarryoverReportViewModelKeys, NegativeCarryoverReportSortKeys> = {
                    id: 'id',
                    agentUid: 'agent_uid',
                    debtAfterPayment: 'debt_after_payment',
                    debtBeforePayment: 'debt_before_payment',
                    debtTransaction: 'debt_transaction',
                    totalNetShare: 'total_net_share',
                    weekStartDateTs: 'week_start_date_ts.seconds',
                    weeklyActuallyPaid: 'weekly_actually_paid',
                };
                return mapper[m];
            })
        );

        createMap<NegativeCarryoverReportSortKeys, NegativeCarryoverReportViewModelKeys>(
            mapper,
            negativeCarryoverReportMappingKeys.sortKeys,
            negativeCarryoverReportMappingKeys.viewModelKeys,
            constructUsing(m => {
                const mapper: Record<NegativeCarryoverReportSortKeys, NegativeCarryoverReportViewModelKeys> = {
                    id: 'id',
                    agent_uid: 'agentUid',
                    debt_after_payment: 'debtAfterPayment',
                    debt_before_payment: 'debtBeforePayment',
                    debt_transaction: 'debtTransaction',
                    total_net_share: 'totalNetShare',
                    'week_start_date_ts.seconds': 'weekStartDateTs',
                    'week_start_date_ts.nanos': 'weekStartDateTs',
                    weekly_actually_paid: 'weeklyActuallyPaid',
                };
                return mapper[m];
            })
        );

        createMap<WeeklySummaryExport, NegativeCarryoverWeeklySummaryDownloadModel>(
            mapper,
            negativeCarryoverReportMappingKeys.weeklySummaryServerModel,
            negativeCarryoverReportMappingKeys.weeklySummaryDownloadModel,
            forMember(
                vm => vm.aal,
                mapFrom(m => m.aal)
            ),
            forMember(
                vm => vm.agent_uid,
                mapFrom(m => m.agent_uid)
            ),
            forMember(
                vm => vm.comments,
                mapFrom(m => m.comments)
            ),
            forMember(
                vm => vm.direction,
                mapFrom(m => m.direction)
            ),
            forMember(
                vm => vm.reason_codes,
                mapFrom(m => m.reason_codes)
            ),
            forMember(
                vm => vm.reference,
                mapFrom(m => m.reference)
            ),
            forMember(
                vm => vm.turnover_rate,
                mapFrom(m => m.turnover_rate?.toString() ?? '0')
            ),
            forMember(
                vm => vm.weekly_actually_paid,
                mapFrom(m => m.weekly_actually_paid?.toFixed(2))
            )
        );
        createMap<NegativeCarryoverWeeklySummaryViewModelKeys, NegativeCarryoverWeeklySummaryQueryFields>(
            mapper,
            negativeCarryoverReportMappingKeys.weeklySummaryViewModelKeys,
            negativeCarryoverReportMappingKeys.weeklySummaryQueryKeys,
            constructUsing(m => {
                const mapper: Record<NegativeCarryoverWeeklySummaryViewModelKeys, NegativeCarryoverWeeklySummaryQueryFields> = {
                    aal: 'items.aal',
                    agent_uid: 'items.agent_uid',
                    comments: 'items.comments',
                    direction: 'items.direction',
                    reason_codes: 'items.reason_codes',
                    reference: 'items.reference',
                    turnover_rate: 'items.turnover_rate',
                    weekly_actually_paid: 'items.weekly_actually_paid',
                };
                return mapper[m];
            })
        );
        createMap<NegativeCarryoverWeeklySummaryViewModelKeys, NegativeCarryoverWeeklySummaryDownloadFields>(
            mapper,
            negativeCarryoverReportMappingKeys.weeklySummaryViewModelKeys,
            negativeCarryoverReportMappingKeys.weeklySummaryDownloadFields,
            constructUsing(m => {
                const mapper: Record<NegativeCarryoverWeeklySummaryViewModelKeys, NegativeCarryoverWeeklySummaryDownloadFields> = {
                    aal: 'aal',
                    agent_uid: 'agent_uid',
                    comments: 'comments',
                    direction: 'direction',
                    reason_codes: 'reason_codes',
                    reference: 'reference',
                    turnover_rate: 'turnover_rate',
                    weekly_actually_paid: 'weekly_actually_paid',
                };
                return mapper[m];
            })
        );
    }
}
